import { SimpleTable, TableSkeleton, TableHeader, DrilldownCell, Alert } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel, severityCodes, SeverityNames } from '@/insights/components/SeverityDropdown'
import { createSeverityHeaders } from '@/insights/components/SeverityHeaderBuilder'
import SingleValueDisplay from '@/insights/components/SingleValueDisplay'
import {
  ApiService,
  InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { formatNumberLocale } from '@/utils'
import { downloadCsv } from '@/utils/csvExport'

import HorizontalChart from './HorizontalChart'

export interface ReeferAlarmCountBySeverityParams extends InsightParametersType {
  slug: string
  companyId?: number
  status: string
  severity?: number[]
  geofenceIds?: number[]
  geofenceCategoryIds?: number[]
  code?: number
  date?: string
  from?: string
  to?: string
  displayFormat: string
}

export interface ReeferAlarmCountData {
  companyId: number
  companyName: string
  critical: number
  warning: number
  informational: number
  total: number
}
const statusOptions = [
  { label: 'All', value: '', isDefault: true },
  { label: 'Active', value: 'active' },
  { label: 'Cleared', value: 'cleared' },
]

const ReeferAlarmActiveBySeverity: InsightDefinition<ReeferAlarmCountBySeverityParams, ReeferAlarmCountData[]> = {
  active: true,
  slug: 'alarm/count-by-severity',
  category: 'reeferAlarm',
  name: 'Reefer Alarm Count by Severity',
  description: 'Display alarm counts by severity',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }

      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'status',
        control: 'CustomSingleSelect',
        label: 'Alarm Status',
        options: statusOptions,
      },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      { id: 'dateRange', control: 'DateTimeRange', dependsOn: 'date', dependsOnValue: 'custom' },
      {
        id: 'displayFormat',
        control: 'CustomSingleSelect',
        options: [
          { label: 'Table', value: 'table', isDefault: true },
          { label: 'Single Value Chart', value: 'single' },
          { label: 'Horizontal Bar Chart', value: 'horizontal' },
        ],
      },
    ],

    isValid: (configuration: ReeferAlarmCountBySeverityParams) => {
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }
      if (configuration?.companyId) {
        return true
      }
      return 'Company is required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferAlarmCountBySeverityParams) => {
    const path = `/insights/alarms/count-by-severity?${buildQuery(parameters)}`
    const response = await api.get<ReeferAlarmCountData[]>({
      path,
      origin: 'AlarmCountBySeverity#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferAlarmCountBySeverityParams, title: string) => {
    const path = '/insights/alarms/count-by-severity'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, configuration, data }) => {
    const parameters = configuration.parameters as ReeferAlarmCountBySeverityParams
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      return <TableSkeleton columns={2 + (parameters.severity?.length || 3)} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    const severities = (
      parameters.severity?.length
        ? parameters.severity.map((s) => getSeverityLabel(String(s)).toLowerCase())
        : ['critical', 'warning', 'informational']
    ) as SeverityNames[]

    if (parameters.displayFormat?.startsWith('single')) {
      const ddConfig: InsightConfigurationType = {
        ...configuration,
        slug: 'alarm/alarms-detail',
        title: `${configuration.title}: Details`,
        parameters: {
          ...parameters,
          displayFormat: 'table',
          isDrilldown: true,
        },
      }
      const value = (data || []).reduce((sum, row) => sum + row.critical + row.informational + row.warning, 0)
      return <SingleValueDisplay value={value} width={configuration.width} drilldownConfiguration={ddConfig} />
    }
    if (parameters.displayFormat?.startsWith('horizontal')) {
      // const ddConfig: InsightConfigurationType = {
      //   ...configuration,
      //   slug: 'alarm/alarms-detail',
      //   title: `${configuration.title}: Details`,
      //   parameters: {
      //     ...parameters,
      //     displayFormat: 'table',
      //     isDrilldown: true,
      //   },
      // }
      return <HorizontalChart data={data ?? []} configuration={configuration} />
    }

    if (data?.length === 0) {
      return null
    }
    const sortedData = [...(data ?? [])].sort((a, b) => b.total - a.total)

    const body = sortedData?.map((item) => {
      const result: Record<string, string | DrilldownCell | React.ReactNode> = {
        Company: <span className="flex pl-1">{item.companyName}</span>,
      }
      severities.forEach((sev) => {
        const code = severityCodes.find((s) => s.label.toLowerCase() === sev)?.value
        const ddConfig: InsightConfigurationType = {
          ...configuration,
          slug: 'alarm/alarms-detail',
          title: `${item.companyName} - ${configuration.title} Details`,
          parameters: {
            ...parameters,
            companyId: item.companyId,
            displayFormat: 'table',
            severity: code,
            isDrilldown: true,
          },
        }
        result[sev] =
          Number(item[sev]) > 0 ? (
            {
              value: formatNumberLocale(Number(item[sev])),
              className: 'block pr-5 w-full text-center',
              configuration: ddConfig,
              id: `${item.companyId}-${sev}`,
            }
          ) : (
            <span className="block pr-5 w-full text-center">{formatNumberLocale(Number(item[sev]))}</span>
          )
      })
      return result
    })

    const sevHeaders = createSeverityHeaders({
      selectedSeverities: severities,
      includeIcons: true,
      includeTotal: false,
    }) as TableHeader[]
    const headers = ['Company', ...sevHeaders]
    const headerClassNames = sevHeaders.reduce((h, s) => Object.assign(h, { [s.key]: 'w-[100px]' }), {})

    return (
      <section className="text-base-content text-base h-full flex flex-col ">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} headerClassNames={headerClassNames} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const shouldShowLabels = !['25%', '33%'].includes(configuration.width)

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as ReeferAlarmCountBySeverityParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.severity?.length) {
      const labels = params.severity
        .map((s) => getSeverityLabel(s.toString()))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    if (params.status) {
      const { status } = params
      const statusDisplay = statusOptions.find((o) => status === o.value)?.label
      if (statusDisplay) {
        fields['Status'] = statusDisplay
      }
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} showLabel={shouldShowLabels} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ configuration, data, status }) => {
    if ((configuration.parameters as ReeferAlarmCountBySeverityParams).displayFormat?.startsWith('single')) {
      return ''
    }
    if ((status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferAlarmActiveBySeverity
